import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventService } from 'src/app/core/services/event.service';
import { Footer } from 'src/app/shared/footer/footer';
import { SidebarComponent } from 'src/app/shared/sidebar/sidebar.component';
import { TopbarComponent } from 'src/app/shared/topbar/topbar.component';
import { BreadcrumbService } from 'src/app/shared/page-title/breadcrumb.service';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';

@Component({
  selector: 'app-general-layout',
  standalone: true,
  imports: [SidebarComponent, TopbarComponent, Footer, RouterOutlet],
  templateUrl: './general.html',
  styleUrl: './general.scss',
})
export class GeneralLayout implements OnInit {
  private readonly eventService = inject(EventService);
  private readonly router = inject(Router);
  private readonly breadcrumbs = inject(BreadcrumbService);
  private readonly destroyRef = inject(DestroyRef);

  topbar = 'dark';
  isCondensed = false;
  readonly isNotFoundRoute = signal(false);
  readonly isForceResetPasswordRoute = signal(false);

  ngOnInit() {
    this.isNotFoundRoute.set(this.router.url.startsWith('/404'));
    this.isForceResetPasswordRoute.set(this.router.url.startsWith('/force-reset-password'));
    // The one place the breadcrumb is resolved: once for the page that loaded, then once per
    // navigation below. Every page's `app-page-title` reads the result instead of asking again.
    this.breadcrumbs.resolveFor(this.router.url).catch(() => undefined);
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        this.isNotFoundRoute.set(e.urlAfterRedirects.startsWith('/404'));
        this.isForceResetPasswordRoute.set(e.urlAfterRedirects.startsWith('/force-reset-password'));
        this.breadcrumbs.resolveFor(e.urlAfterRedirects).catch(() => undefined);
        // Skote convention: close mobile sidebar on every navigation.
        document.body.classList.remove('sidebar-enable');
      });

    const topbarSub = this.eventService.subscribe('changeTopbar', (topbar: unknown) => {
      this.topbar = topbar as string;
      this.changeTopbar(this.topbar);
    });
    this.destroyRef.onDestroy(() => topbarSub.unsubscribe());

    document.body.dataset['layout'] = 'vertical';
    document.body.dataset['sidebar'] = 'dark';
    document.body.dataset['layoutSize'] = 'fluid';
    document.body.dataset['sidebarSize'] = 'lg';
    delete document.body.dataset['keepEnlarged'];
    delete document.body.dataset['sidebarSmall'];

    this.changeTopbar(this.topbar);
  }

  onSettingsButtonClicked() {
    document.body.classList.toggle('right-bar-enabled');
  }

  changeTopbar(topbar: string) {
    document.body.dataset['topbar'] = 'dark';

    if (topbar === 'colored') {
      document.body.dataset['topbar'] = 'colored';
    } else {
      document.body.dataset['topbar'] = 'dark';
    }
  }

  onToggleMobileMenu() {
    this.isCondensed = !this.isCondensed;
    document.body.classList.toggle('sidebar-enable');
    document.body.classList.toggle('vertical-collpsed');

    if (window.screen.width <= 768) {
      document.body.classList.remove('vertical-collpsed');
    }
  }
}
